import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Form, Button, Spinner, Alert, Modal } from 'react-bootstrap';

import coldemailer_api from './coldemailer_api';
import coldemailer_config from './coldemailer_config';
import './coldemailer.css';
import './coldemailer-styles.css';

// Fixed, as the brief specifies. The server accepts a page size but the view does not offer one.
const c_RowsPerPage = 20;

// Typing refetches, so a request waits until the user pauses
const c_SearchDebounceMs = 300;

// Every toggle starts off, so the first load shows the whole table. The keys are the query
// parameter names the endpoint reads. Four narrow the table by removing rows; has_email narrows it
// by requiring one, but it behaves the same way from here, so it rides along with them.
const c_ExclusionFilters = [
	{ key: 'exclude_gov_municipal', label: 'Hide gov / municipal' },
	{ key: 'exclude_vertical', label: 'Hide real estate / auto / auction' },
	{ key: 'exclude_junk_names', label: 'Hide junk names' },
	{ key: 'exclude_non_ascii', label: 'Hide non-ASCII names' },
	{ key: 'has_email', label: 'Has email' }
];

const c_EmptyExclusions = {
	exclude_gov_municipal: false,
	exclude_vertical: false,
	exclude_junk_names: false,
	exclude_non_ascii: false,
	has_email: false
};

// The status columns offered as dropdowns. The options themselves are not listed here: they come
// from the endpoint, which reads the distinct values held in the data.
const c_StatusFilters = [
	{ key: 'enrichment_status', label: 'Enrichment' },
	{ key: 'data_status', label: 'Data' },
	{ key: 'qualification_status', label: 'Qualification' }
];

// An empty value means the column is not filtered on, and the endpoint adds no clause for it
const c_EmptyStatusFilters = {
	enrichment_status: '',
	data_status: '',
	qualification_status: ''
};

// Everything the view remembers between visits, and equally what the Clear button puts back. One
// object so the two can never drift apart.
const c_DefaultFilterState = {
	search_text: '',
	niche_text: '',
	industry_text: '',
	page: 1,
	exclusions: c_EmptyExclusions,
	status_filters: c_EmptyStatusFilters
};

// Read back over the defaults rather than used as it stands, so a key added to the state later, or
// a stored value left by an older version of the view, cannot leave a field undefined.
const load_filter_state = () => {
	try {
		const stored = localStorage.getItem(coldemailer_config.lead_discovery_filters_key);
		if(!stored) {
			return c_DefaultFilterState;
		}

		const parsed = JSON.parse(stored);
		if(!parsed || (typeof parsed !== 'object')) {
			return c_DefaultFilterState;
		}

		return Object.assign({}, c_DefaultFilterState, parsed, {
			exclusions: Object.assign({}, c_EmptyExclusions, parsed.exclusions),
			status_filters: Object.assign({}, c_EmptyStatusFilters, parsed.status_filters)
		});
	} catch (error) {
		console.log('LeadDiscoveryView.load_filter_state - ERROR: ', error);
		return c_DefaultFilterState;
	}
};

const save_filter_state = (filter_state) => {
	try {
		localStorage.setItem(coldemailer_config.lead_discovery_filters_key, JSON.stringify(filter_state));
	} catch (error) {
		console.log('LeadDiscoveryView.save_filter_state - ERROR: ', error);
	}
};

// Scraped addresses carry the markup they sat in on the page, so an address lifted from bold text
// is stored as **name@example.com**. Stripped for display only; the stored value is not touched.
const clean_email = (value) => {
	return String(value).replace(/[*"'`]/g, '').trim();
};

const format_date = (value) => {
	if(!value) {
		return '-';
	}

	const date = new Date(value);
	if(isNaN(date.getTime())) {
		return '-';
	}

	return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

// The endpoint turns a filter on only for the exact text 'true', so an unchecked toggle sends
// nothing at all rather than the word 'false'
const to_query_flag = (is_on) => {
	if(is_on) {
		return 'true';
	}

	return '';
};

function LeadDiscoveryView() {
	const navigate = useNavigate();

	const [companies, set_companies] = useState([]);
	const [total, set_total] = useState(0);
	const [loading, set_loading] = useState(true);
	const [error, set_error] = useState('');
	// Passed as functions so storage is read once on mount rather than on every render. A refresh
	// or a return from another view therefore resumes the same search.
	const [search_text, set_search_text] = useState(function() { return load_filter_state().search_text; });
	const [niche_text, set_niche_text] = useState(function() { return load_filter_state().niche_text; });
	const [industry_text, set_industry_text] = useState(function() { return load_filter_state().industry_text; });
	const [exclusions, set_exclusions] = useState(function() { return load_filter_state().exclusions; });
	const [status_filters, set_status_filters] = useState(function() { return load_filter_state().status_filters; });
	const [status_options, set_status_options] = useState({});

	// Add to list modal. Its state is deliberately separate from the filter state, which it never
	// touches: opening or confirming leaves the table exactly as the user left it.
	const [show_add_modal, set_show_add_modal] = useState(false);
	const [add_list_name, set_add_list_name] = useState('');
	const [add_description, set_add_description] = useState('');
	const [add_match_total, set_add_match_total] = useState(null);
	const [add_email_total, set_add_email_total] = useState(null);
	const [add_counting, set_add_counting] = useState(false);
	const [add_saving, set_add_saving] = useState(false);
	const [add_error, set_add_error] = useState('');
	const [add_result, set_add_result] = useState(null);
	const [page, set_page] = useState(function() { return load_filter_state().page; });

	// Everything the endpoint filters on, without paging. Built in one place so the table, the
	// modal's count and the add request can never end up asking about different sets of rows.
	const build_current_filters = () => {
		return {
			search: search_text,
			niche: niche_text,
			industry: industry_text,
			enrichment_status: status_filters.enrichment_status,
			data_status: status_filters.data_status,
			qualification_status: status_filters.qualification_status,
			exclude_gov_municipal: to_query_flag(exclusions.exclude_gov_municipal),
			exclude_vertical: to_query_flag(exclusions.exclude_vertical),
			exclude_junk_names: to_query_flag(exclusions.exclude_junk_names),
			exclude_non_ascii: to_query_flag(exclusions.exclude_non_ascii),
			has_email: to_query_flag(exclusions.has_email)
		};
	};

	// Paging and filtering happen on the server, so every change refetches rather than slicing
	// what is already loaded
	const load_companies = async () => {
		set_loading(true);
		set_error('');

		try {
			const result = await coldemailer_api.get_companies({
				page: page,
				page_size: c_RowsPerPage,
				search: search_text,
				niche: niche_text,
				industry: industry_text,
				enrichment_status: status_filters.enrichment_status,
				data_status: status_filters.data_status,
				qualification_status: status_filters.qualification_status,
				exclude_gov_municipal: to_query_flag(exclusions.exclude_gov_municipal),
				exclude_vertical: to_query_flag(exclusions.exclude_vertical),
				exclude_junk_names: to_query_flag(exclusions.exclude_junk_names),
				exclude_non_ascii: to_query_flag(exclusions.exclude_non_ascii),
				has_email: to_query_flag(exclusions.has_email)
			});

			set_companies(result.rows);
			set_total(result.total);
		} catch (error) {
			console.log('LeadDiscoveryView.load_companies - ERROR: ', error);
			set_error('Failed to load companies.');
		} finally {
			set_loading(false);
		}
	};

	// The dropdown options come from the data and do not change while the view is open, so they
	// are fetched once rather than alongside every search
	const load_status_options = async () => {
		try {
			const values = await coldemailer_api.get_company_status_values();
			set_status_options(values);
		} catch (error) {
			console.log('LeadDiscoveryView.load_status_options - ERROR: ', error);
		}
	};

	useEffect(function() {
		load_status_options();
	}, []);

	// Saved on every change, so whatever the view is showing is what it comes back to
	useEffect(function() {
		save_filter_state({
			search_text,
			niche_text,
			industry_text,
			page,
			exclusions,
			status_filters
		});
	}, [page, search_text, niche_text, industry_text, exclusions, status_filters]);

	useEffect(function() {
		const timer = setTimeout(function() {
			load_companies();
		}, c_SearchDebounceMs);

		return function() {
			clearTimeout(timer);
		};
	}, [page, search_text, niche_text, industry_text, exclusions, status_filters]);

	const total_pages = Math.max(1, Math.ceil(total / c_RowsPerPage));
	const first_row = (page - 1) * c_RowsPerPage;

	const has_active_filter = () => {
		if(search_text || niche_text || industry_text) {
			return true;
		}

		for(var filter_index = 0; filter_index < c_ExclusionFilters.length; ++filter_index) {
			if(exclusions[c_ExclusionFilters[filter_index].key]) {
				return true;
			}
		}

		for(var status_index = 0; status_index < c_StatusFilters.length; ++status_index) {
			if(status_filters[c_StatusFilters[status_index].key]) {
				return true;
			}
		}

		return false;
	};

	// Any change to what is being asked for puts the user back on the first page, since the page
	// they were on may not exist under the new filters
	const on_search_change = (event) => {
		set_search_text(event.target.value);
		set_page(1);
	};

	const on_niche_change = (event) => {
		set_niche_text(event.target.value);
		set_page(1);
	};

	const on_industry_change = (event) => {
		set_industry_text(event.target.value);
		set_page(1);
	};

	const on_status_filter_change = (filter_key) => {
		return (event) => {
			const value = event.target.value;

			set_status_filters(function(previous) {
				return Object.assign({}, previous, { [filter_key]: value });
			});

			set_page(1);
		};
	};

	const on_exclusion_change = (filter_key) => {
		return (event) => {
			const is_checked = event.target.checked;

			set_exclusions(function(previous) {
				return Object.assign({}, previous, { [filter_key]: is_checked });
			});

			set_page(1);
		};
	};

	// Puts every control back to its default. The save effect then writes the defaults out, so a
	// later visit starts clean rather than restoring what was just cleared.
	const on_clear_filters = () => {
		set_search_text(c_DefaultFilterState.search_text);
		set_niche_text(c_DefaultFilterState.niche_text);
		set_industry_text(c_DefaultFilterState.industry_text);
		set_exclusions(c_DefaultFilterState.exclusions);
		set_status_filters(c_DefaultFilterState.status_filters);
		set_page(c_DefaultFilterState.page);
	};

	// The two counts the modal shows. Both are read now rather than one being taken from the last
	// table fetch: the scraper keeps adding rows, so a total from a minute ago can be smaller than
	// a with-email count read this second, which reads as nonsense.
	const load_add_email_total = async () => {
		set_add_counting(true);

		try {
			const match_filters = build_current_filters();
			match_filters.page = 1;
			match_filters.page_size = 1;

			const match_result = await coldemailer_api.get_companies(match_filters);
			set_add_match_total(match_result.total);

			const email_filters = build_current_filters();
			email_filters.has_email = 'true';
			email_filters.page = 1;
			email_filters.page_size = 1;

			const email_result = await coldemailer_api.get_companies(email_filters);
			set_add_email_total(email_result.total);
		} catch (error) {
			console.log('LeadDiscoveryView.load_add_email_total - ERROR: ', error);
			set_add_match_total(null);
			set_add_email_total(null);
		} finally {
			set_add_counting(false);
		}
	};

	// The name and description are deliberately left as they were, so adding a second batch to the
	// same list does not mean typing its name again. Only the per-run state is cleared.
	const on_open_add_modal = () => {
		set_add_email_total(null);
		set_add_error('');
		set_add_result(null);
		set_show_add_modal(true);

		load_add_email_total();
	};

	const on_hide_add_modal = () => {
		if(add_saving) {
			return;
		}

		set_show_add_modal(false);
	};

	const on_add_list_name_change = (event) => {
		set_add_list_name(event.target.value);
	};

	const on_add_description_change = (event) => {
		set_add_description(event.target.value);
	};

	// The backend pages the companies endpoint itself, so this is one request no matter how many
	// companies match
	const on_confirm_add = async () => {
		set_add_saving(true);
		set_add_error('');

		try {
			const result = await coldemailer_api.add_companies_to_list({
				list_name: add_list_name.trim(),
				description: add_description.trim(),
				filters: build_current_filters()
			});

			set_add_result(result);
		} catch (error) {
			console.log('LeadDiscoveryView.on_confirm_add - ERROR: ', error);

			const message = (error.response && error.response.data && error.response.data.message) ?
								error.response.data.message : 'Failed to add the companies to the list.';
			set_add_error(message);
		} finally {
			set_add_saving(false);
		}
	};

	const on_row_click = (company) => {
		navigate(coldemailer_config.lead_discovery_route + '/' + company.id);
	};

	// The url cell is a link in its own right, so its click must not also open the detail view
	const on_url_click = (event) => {
		event.stopPropagation();
	};

	const on_previous_page = () => {
		if(page > 1) {
			set_page(page - 1);
		}
	};

	const on_next_page = () => {
		if(page < total_pages) {
			set_page(page + 1);
		}
	};

	const render_header = () => {
		return (
			<div className="ce-header">
				<div className="ce-header-title-group">
					<div>
						<h4 className="ce-title">Lead Discovery</h4>
						<p className="ce-subtitle">Browse discovered companies</p>
					</div>

					{/* Sits by the title rather than with Refresh and Clear, so the button that
					    writes a list is nowhere near the one that wipes the filters */}
					<Button size="sm" className="ce-button-create" onClick={on_open_add_modal}>
						Add to ColdEmailer
					</Button>
				</div>

				<div className="ce-header-actions">
					<Button variant="outline-light" size="sm" className="ce-button" onClick={load_companies}>
						Refresh
					</Button>

					<Button size="sm" className="ce-button-create" onClick={on_clear_filters}>
						Clear
					</Button>
				</div>
			</div>
		);
	};

	const render_toolbar = () => {
		return (
			<div className="ce-toolbar">
				<Form.Group className="ce-field ce-field-search">
					<Form.Label className="ce-label">Search</Form.Label>
					<Form.Control
						type="text"
						placeholder="Filter by name or url"
						value={search_text}
						onChange={on_search_change}
					/>
				</Form.Group>

				<Form.Group className="ce-field ce-field-niche">
					<Form.Label className="ce-label">Niche</Form.Label>
					<Form.Control
						type="text"
						placeholder="Filter by niche"
						value={niche_text}
						onChange={on_niche_change}
					/>
				</Form.Group>

				<Form.Group className="ce-field ce-field-industry">
					<Form.Label className="ce-label">Industry</Form.Label>
					<Form.Control
						type="text"
						placeholder="Filter by industry"
						value={industry_text}
						onChange={on_industry_change}
					/>
				</Form.Group>

				<div className="ce-spacer"></div>
			</div>
		);
	};

	const render_filter_toggle = (filter) => {
		return (
			<Form.Check
				key={filter.key}
				type="checkbox"
				id={'ld-filter-' + filter.key}
				className="ce-filter-toggle"
				label={filter.label}
				checked={exclusions[filter.key]}
				onChange={on_exclusion_change(filter.key)}
			/>
		);
	};

	const render_status_filter = (filter) => {
		const options = status_options[filter.key] ? status_options[filter.key] : [];

		return (
			<Form.Group key={filter.key} className="ce-field ce-field-status">
				<Form.Label className="ce-label">{filter.label}</Form.Label>
				<Form.Select
					value={status_filters[filter.key]}
					onChange={on_status_filter_change(filter.key)}
				>
					<option value="">All</option>
					{options.map(function(option) {
						return (
							<option key={option} value={option}>
								{option}
							</option>
						);
					})}
				</Form.Select>
			</Form.Group>
		);
	};

	const render_status_bar = () => {
		return (
			<div className="ce-status-bar">
				{c_StatusFilters.map(render_status_filter)}
			</div>
		);
	};

	const render_filter_bar = () => {
		return (
			<div className="ce-filter-bar">
				{c_ExclusionFilters.map(render_filter_toggle)}
			</div>
		);
	};

	const render_url_cell = (company) => {
		if(!company.url) {
			return '-';
		}

		return (
			<a
				href={company.url}
				target="_blank"
				rel="noopener noreferrer"
				className="ce-link"
				onClick={on_url_click}
			>
				{company.url}
			</a>
		);
	};

	const render_empty_row = () => {
		return (
			<tr>
				<td colSpan={7} className="ce-empty">
					{has_active_filter() ? '0 companies match' : 'No companies yet.'}
				</td>
			</tr>
		);
	};

	const render_row = (company) => {
		return (
			<tr key={company.id} className="ce-row-clickable" onClick={function() { on_row_click(company); }}>
				<td className="ce-ld-col-name">
					{company.name ? company.name : '-'}
				</td>
				<td className="ce-ld-col-url">
					{render_url_cell(company)}
				</td>
				<td className="ce-ld-col-niche">
					{company.niche ? company.niche : '-'}
				</td>
				<td className="ce-ld-col-email">
					{company.email ? clean_email(company.email) : '-'}
				</td>
				<td className="ce-ld-col-employees">
					{company.num_employees === null || company.num_employees === undefined ? '-' : company.num_employees}
				</td>
				<td className="ce-ld-col-qualification" title={company.qualification_status ? company.qualification_status : ''}>
					{company.qualification_status ? company.qualification_status : '-'}
				</td>
				<td className="ce-ld-col-discovered">
					{format_date(company.discovered_at)}
				</td>
			</tr>
		);
	};

	const render_table = () => {
		return (
			<Table striped bordered hover responsive variant="dark" className="ce-table">
				<thead>
					<tr>
						<th className="ce-ld-col-name">Name</th>
						<th className="ce-ld-col-url">Url</th>
						<th className="ce-ld-col-niche">Niche</th>
						<th className="ce-ld-col-email">Email</th>
						<th className="ce-ld-col-employees" title="Employees">Employees</th>
						<th className="ce-ld-col-qualification" title="Qualification">Qualification</th>
						<th className="ce-ld-col-discovered" title="Discovered">Discovered</th>
					</tr>
				</thead>
				<tbody>
					{companies.length === 0 ? render_empty_row() : companies.map(render_row)}
				</tbody>
			</Table>
		);
	};

	const render_pagination = () => {
		const last_row = Math.min(first_row + c_RowsPerPage, total);
		const showing_from = total === 0 ? 0 : first_row + 1;

		return (
			<div className="ce-pagination">
				<div className="ce-pagination-info">
					{'Showing ' + showing_from + ' to ' + last_row + ' of ' + total}
				</div>

				<div className="ce-pagination-controls">
					<Button
						variant="outline-light"
						size="sm" className="ce-button"
						disabled={page === 1}
						onClick={on_previous_page}
					>
						Previous
					</Button>

					<span className="ce-page-label">
						{'Page ' + page + ' of ' + total_pages}
					</span>

					<Button
						variant="outline-light"
						size="sm" className="ce-button"
						disabled={page >= total_pages}
						onClick={on_next_page}
					>
						Next
					</Button>
				</div>
			</div>
		);
	};

	const render_loading = () => {
		return (
			<div className="ce-loading">
				<Spinner animation="border" size="sm" />
			</div>
		);
	};

	const render_error = () => {
		return (
			<Alert variant="danger">
				{error}
			</Alert>
		);
	};

	// The filtered total, which is what the pagination counts against
	const render_total = () => {
		return (
			<div className="ce-total-count">
				{total + (total === 1 ? ' company' : ' companies')}
			</div>
		);
	};

	const render_panel = () => {
		return (
			<div className="ce-panel">
				{render_total()}
				{render_toolbar()}
				{render_status_bar()}
				{render_filter_bar()}
				{loading ? render_loading() : render_table()}
				{render_pagination()}
			</div>
		);
	};

	const render_add_counts = () => {
		if(add_counting) {
			return (
				<p className="ce-modal-counts">
					Counting companies...
				</p>
			);
		}

		if((add_match_total === null) || (add_email_total === null)) {
			return (
				<p className="ce-modal-counts">
					The counts could not be read. Confirming will still add every matching company.
				</p>
			);
		}

		return (
			<p className="ce-modal-counts">
				{add_match_total + ' companies match, ' + add_email_total + ' with email'}
			</p>
		);
	};

	const render_add_form = () => {
		return (
			<React.Fragment>
				{render_add_counts()}

				<Form>
					<Form.Group className="ce-modal-field">
						<Form.Label className="ce-modal-label">List Name</Form.Label>
						<Form.Control
							type="text"
							placeholder="Name of the list to add to"
							value={add_list_name}
							onChange={on_add_list_name_change}
							disabled={add_saving}
						/>
						<Form.Text className="ce-modal-help">
							An existing list with this name is added to. A new one is created otherwise.
						</Form.Text>
					</Form.Group>

					<Form.Group className="ce-modal-field">
						<Form.Label className="ce-modal-label">Description</Form.Label>
						<Form.Control
							type="text"
							placeholder="What this list is for"
							value={add_description}
							onChange={on_add_description_change}
							disabled={add_saving}
						/>
					</Form.Group>
				</Form>
			</React.Fragment>
		);
	};

	const render_add_result_row = (label, value) => {
		return (
			<div className="ce-detail-row">
				<div className="ce-detail-label">{label}</div>
				<div className="ce-detail-value">{value}</div>
			</div>
		);
	};

	const render_add_result = () => {
		return (
			<React.Fragment>
				{render_add_result_row('Matched', add_result.matched)}
				{render_add_result_row('Dropped (invalid email)', add_result.dropped_invalid_email)}
				{render_add_result_row('Deduplicated', add_result.deduped_in_batch)}
				{render_add_result_row('Submitted', add_result.submitted)}
				{render_add_result_row('Inserted', add_result.inserted)}
				{render_add_result_row('Duplicates (already in list)', add_result.duplicates)}
				{render_add_result_row('List', add_result.list_created ? 'Created' : 'Added to')}
			</React.Fragment>
		);
	};

	const render_add_modal_footer = () => {
		if(add_result) {
			return (
				<Button variant="outline-secondary" onClick={on_hide_add_modal}>
					Close
				</Button>
			);
		}

		return (
			<React.Fragment>
				<Button variant="outline-secondary" onClick={on_hide_add_modal} disabled={add_saving}>
					Cancel
				</Button>

				<Button
					className="ce-button-create"
					onClick={on_confirm_add}
					disabled={!add_list_name.trim() || add_saving}
				>
					{add_saving ? 'Adding...' : 'Confirm'}
				</Button>
			</React.Fragment>
		);
	};

	const render_add_modal = () => {
		return (
			<Modal show={show_add_modal} onHide={on_hide_add_modal} centered dialogClassName="ce-modal-dialog">
				<Modal.Header closeButton>
					<Modal.Title>Add to ColdEmailer list</Modal.Title>
				</Modal.Header>

				<Modal.Body className="ce-modal-body">
					{add_error ? <Alert variant="danger" className="ce-modal-alert">{add_error}</Alert> : null}
					{add_result ? render_add_result() : render_add_form()}
				</Modal.Body>

				<Modal.Footer>
					{render_add_modal_footer()}
				</Modal.Footer>
			</Modal>
		);
	};

	// Set as a custom property so the host app's header height stays configurable
	const root_style = { '--ce-top-offset': coldemailer_config.top_offset_px + 'px' };

	return (
		<div className="ce-root ce-root-wide" style={root_style}>
			{render_header()}

			{error ? render_error() : null}

			{render_panel()}

			{render_add_modal()}
		</div>
	);
}

export default LeadDiscoveryView;
